/**
 * カテゴリ語彙の読み込み。
 *
 * 語彙は「少数の粗いもの」（docs/01-requirements.md §4.3）なので、
 * ページごとに全件を素朴に読む。ページングもキャッシュ層も要らない。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@app/api/client';
import type { Category } from '@shared/types';

export interface CategoriesState {
  categories: Category[];
  loading: boolean;
  error: unknown;
  reload: () => void;
  /** id からカテゴリ名を引く。未知の id は null。 */
  nameOf: (id: number | null) => string | null;
}

export function useCategories(): CategoriesState {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listCategories()
      .then((list) => {
        if (!cancelled) {
          setCategories(list);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [generation]);

  const reload = useCallback(() => setGeneration((n) => n + 1), []);

  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const nameOf = useCallback(
    (id: number | null) => (id == null ? null : (byId.get(id)?.name ?? null)),
    [byId],
  );

  return { categories, loading, error, reload, nameOf };
}
